// v2/layout/Sidebar.tsx — Desktop sidebar navigation

import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  FileText,
  CheckSquare,
  UsersRound,
  History,
  Settings,
  Calculator,
  PanelLeftClose,
  PanelLeft,
} from 'lucide-react';
import { Action } from '../shared/types/domain';
import { useAuth } from 'context/AuthContext';
import { usePermission } from '../shared/hooks/usePermission';
import { Role } from '../shared/types/domain';

interface NavItem {
  label: string;
  to: string;
  icon: React.ReactNode;
  /** If set, the link is only shown when this action is allowed */
  requiredAction?: Action;
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', to: '/v2/', icon: <LayoutDashboard className="h-5 w-5" /> },
  { label: 'Leads', to: '/v2/leads', icon: <Users className="h-5 w-5" /> },
  { label: 'Applications', to: '/v2/applications', icon: <FileText className="h-5 w-5" /> },
  { label: 'Tasks', to: '/v2/tasks', icon: <CheckSquare className="h-5 w-5" /> },
  { label: 'Groups', to: '/v2/groups', icon: <UsersRound className="h-5 w-5" /> },
  { label: 'EMI Calculator', to: '/v2/emi-calculator', icon: <Calculator className="h-5 w-5" /> },
  { label: 'Login History', to: '/v2/login-history', icon: <History className="h-5 w-5" />, requiredAction: Action.VIEW_USER_LOGIN_HISTORY },
  { label: 'Settings', to: '/v2/settings', icon: <Settings className="h-5 w-5" /> },
];

function PermissionGatedLink({ item, collapsed }: { item: NavItem; collapsed: boolean }): React.ReactElement | null {
  const { userDetails } = useAuth();
  const role = (userDetails?.role as Role) ?? Role.SALES_USER;
  const isAllowed = usePermission(role, item.requiredAction!, undefined, undefined);

  if (!isAllowed) return null;
  return <SidebarLink item={item} collapsed={collapsed} />;
}

function SidebarLink({ item, collapsed }: { item: NavItem; collapsed: boolean }): React.ReactElement {
  return (
    <NavLink
      to={item.to}
      end={item.to === '/v2/'}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors min-h-[44px] ${
          isActive
            ? 'bg-white/15 text-white'
            : 'text-slate-300 hover:bg-white/10 hover:text-white'
        } ${collapsed ? 'justify-center' : ''}`
      }
      title={collapsed ? item.label : undefined}
    >
      {item.icon}
      {!collapsed && <span>{item.label}</span>}
    </NavLink>
  );
}


export function Sidebar(): React.ReactElement {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <aside
      className={`hidden md:flex flex-col border-r transition-all duration-200 ${
        collapsed ? 'w-16' : 'w-60'
      }`}
      style={{ backgroundColor: 'var(--color-sidebar-bg, #0f172a)', color: 'var(--color-sidebar-fg, #e2e8f0)' }}
    >
      <div className="flex items-center justify-end p-2">
        <button
          onClick={() => setCollapsed((c) => !c)}
          className="p-2 rounded-lg hover:bg-white/10 text-slate-400 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <PanelLeft className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
        </button>
      </div>

      <nav className="flex-1 flex flex-col gap-1 px-2">
        {NAV_ITEMS.map((item) =>
          item.requiredAction ? (
            <PermissionGatedLink key={item.to} item={item} collapsed={collapsed} />
          ) : (
            <SidebarLink key={item.to} item={item} collapsed={collapsed} />
          ),
        )}
      </nav>
    </aside>
  );
}
